import { getApiData } from "../utils/APIHelper";

const SCOPE_ENDPOINTS = {
    GET_PROJECT_SCOPES: (projectId, scopeFilter = null) => {
        const baseUrl = `api/project/${projectId}/scopes`;
        return scopeFilter ? `${baseUrl}?scope_filter=${scopeFilter}` : baseUrl;
    },
    CREATE_SCOPE: (projectId) => `api/project/${projectId}/scope`,
    UPDATE_SCOPE: (scopeId) => `api/project/scope/${scopeId}`,
    DELETE_SCOPE: (scopeId) => `api/project/scope/${scopeId}`,
    GET_SCOPE_DETAILS: (scopeId) => `api/project/scope/${scopeId}/boqs`,
    GET_PROJECT_ROOM_BOQS: (projectId) => `api/project/${projectId}/rooms/boqs`,
    GET_SCOPE_BOQS: (projectId, scopeId) => `api/project/${projectId}/scope/boqs${scopeId ? `?scope_id=${scopeId}` : ''}`,
    GET_SCOPE_ANALYTICS: (projectId, scopeFilter = null) => {
        const baseUrl = `api/project/${projectId}/scope/analytics`;
        return scopeFilter ? `${baseUrl}?scope_filter=${scopeFilter}` : baseUrl;
    },
};

/**
 * Get all scopes for a project
 * @param {string} projectId - Project ID
 * @param {string} scopeFilter - Optional filter: "CREATED", "BID", "CONTRACTED", or null for all
 * @returns {Promise} API response
 */
export const getProjectScopes = async (projectId, scopeFilter = null) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.GET_PROJECT_SCOPES(projectId, scopeFilter), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching project scopes:", error);
        throw error;
    }
};

/**
 * Create a new scope for a project
 * @param {string} projectId - Project ID
 * @param {Object} scopeData - Scope data
 * @param {string} scopeData.name - Scope name
 * @param {string} scopeData.description - Scope description
 * @param {Array} scopeData.references - References array with refType and Ids
 * @returns {Promise} API response
 */
export const createScope = async (projectId, scopeData) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.CREATE_SCOPE(projectId), "POST", scopeData);
        return response;
    } catch (error) {
        console.error("Error creating scope:", error);
        throw error;
    }
};

/**
 * Update an existing scope
 * @param {string} scopeId - Scope ID
 * @param {Object} scopeData - Scope data
 * @param {string} scopeData.name - Scope name
 * @param {string} scopeData.description - Scope description
 * @param {Array} scopeData.references - References array with refType and Ids
 * @returns {Promise} API response
 */
export const updateScope = async (scopeId, scopeData) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.UPDATE_SCOPE(scopeId), "PUT", scopeData);
        return response;
    } catch (error) {
        console.error("Error updating scope:", error);
        throw error;
    }
};

/**
 * Get scope details with BOQs
 * @param {string} scopeId - Scope ID
 * @returns {Promise} API response
 */
export const getScopeDetails = async (scopeId) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.GET_SCOPE_DETAILS(scopeId), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching scope details:", error);
        throw error;
    }
};

/**
 * Get project room BOQs for scope selection
 * @param {string} projectId - Project ID
 * @returns {Promise} API response
 */
export const getProjectRoomBoqs = async (projectId) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.GET_PROJECT_ROOM_BOQS(projectId), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching project room BOQs:", error);
        throw error;
    }
};

/**
 * Get scope BOQs with nested view support
 * @param {string} projectId - Project ID
 * @param {string} scopeId - Optional scope ID for editing
 * @returns {Promise} API response
 */
export const getScopeBoqs = async (projectId, scopeId = null) => {
    try {
        const endpoint = SCOPE_ENDPOINTS.GET_SCOPE_BOQS(projectId, scopeId);
        const response = await getApiData(endpoint, "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching scope BOQs:", error);
        throw error;
    }
};

/**
 * Delete a scope by id
 * @param {string|number} scopeId - Scope ID
 * @returns {Promise} API response
 */
export const deleteScope = async (scopeId) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.DELETE_SCOPE(scopeId), "DELETE", {});
        return response;
    } catch (error) {
        console.error("Error deleting scope:", error);
        throw error;
    }
};

/**
 * Get scope analytics for a project
 * @param {string|number} projectId - Project ID
 * @param {string|null} scopeFilter - Optional filter ("CREATED" | "BID" | "CONTRACTED")
 * @returns {Promise} API response
 */
export const getScopeAnalytics = async (projectId, scopeFilter = null) => {
    try {
        const response = await getApiData(SCOPE_ENDPOINTS.GET_SCOPE_ANALYTICS(projectId, scopeFilter), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching scope analytics:", error);
        throw error;
    }
};
